// Guest → account transition for the free Level 1 Support Desk.
//
// A signed-out visitor's ticket work lives in the guest namespace (nell:v1:guest:).
// It is temporary and NOT trusted: a "complete" flag in guest storage proves nothing.
// When the visitor signs in and accepts "continue where you left off", each guest
// ticket is moved into the learner record through the learner's own Support Desk
// API (createSupportProgress) — no second progression engine:
//
//   1. Tickets are processed in order; the learner's sequential unlock rule applies
//      (a ticket must be AVAILABLE in the learner record; locked tickets are never touched).
//   2. A ticket the learner has already completed is skipped (no duplicate XP — XP is
//      derived from the number of completed tickets, never added per claim).
//   3. A ticket the learner already has work on is kept as is (account work is never
//      overwritten); processing stops there.
//   4. Guest input is sanitised (known answer fields, guided step values that fit their step
//      definition, bounded text, bounded command list).
//   5. The guest's COMMANDS are replayed on a fresh simulated endpoint; the stored guest
//      output is discarded. Completion is then decided by the normal learner validation
//      (learner.complete → supportValidation) on the answers + replayed evidence.
//      A guest ticket that fails validation is carried over as an in-progress draft only.
//   6. The guest record is cleared only after the whole pass finished without an error.
//      Re-running the claim is safe: completed tickets are skipped, nothing is counted twice.
//
// PROTOTYPE LIMIT: everything still runs in the browser (mock auth, localStorage).
// Someone who edits their own storage can also edit the learner record directly, so
// this protects progression rules and XP accounting, not against a determined user.
// Permanent, cross-device progress needs the server to store tickets per account and
// to run this same validation server-side (see README "Guest progress").
import { SUPPORT_TICKETS, PRACTICE_TICKET } from '../domains/support/tickets.js';
import { replayEvidence } from '../domains/support/endpoint.js';
import { sanitizeGuidedValue } from './guidedTicket.js';
import { ticketVersion, recordVersion } from './supportProgress.js';

export const ANSWER_FIELDS = ['symptom', 'impact', 'scope', 'firstCheck', 'evidence', 'assessment', 'disposition', 'handoff'];
const MAX_TEXT = 4000;
const MAX_COMMANDS = 40;
const MAX_COMMAND_LEN = 120;

function sanitize(rec, ticket) {
  const src = rec && typeof rec === 'object' ? rec : {};
  const a = src.answers && typeof src.answers === 'object' ? src.answers : {};
  const answers = {};
  for (const k of ANSWER_FIELDS) if (typeof a[k] === 'string' && a[k].trim()) answers[k] = a[k].slice(0, MAX_TEXT);
  const commands = (Array.isArray(src.evidence) ? src.evidence : [])
    .map((e) => (e && typeof e.command === 'string' ? e.command.trim() : ''))
    .filter((c) => c && c.length <= MAX_COMMAND_LEN)
    .slice(0, MAX_COMMANDS);
  const g = src.guided && typeof src.guided === 'object' ? src.guided : {};
  const guided = {};
  for (const step of ticket?.guided || []) {
    const v = sanitizeGuidedValue(step, g[step.id]);
    if (v !== undefined) guided[step.id] = v;
  }
  return { answers, guided, commands, claimedComplete: src.status === 'complete' };
}

const hasWork = (r) => Object.keys(r.answers || {}).length > 0 || Object.keys(r.guided || {}).length > 0 || (r.evidence || []).length > 0;
const PRACTICE_QUESTIONS = ['q1', 'q2', 'q3', 'q4', 'q5'];
const hasPracticeWork = (r) => Object.keys(r.answers || {}).length > 0 || !!r.note || (r.events || []).length > 0 || !!r.status;

function sanitizePractice(rec) {
  const src = rec && typeof rec === 'object' ? rec : {};
  const answersIn = src.answers && typeof src.answers === 'object' ? src.answers : {};
  const answers = Object.fromEntries(PRACTICE_QUESTIONS
    .filter((q) => ['A', 'B', 'C', 'D'].includes(answersIn[q]))
    .map((q) => [q, answersIn[q]]));
  const events = (Array.isArray(src.events) ? src.events : []).slice(0, MAX_COMMANDS).flatMap((e) => {
    if (e?.type === 'reconnect') return [{ type: 'reconnect' }];
    if (e?.type === 'command' && typeof e.command === 'string' && e.command.trim() && e.command.length <= MAX_COMMAND_LEN) return [{ type: 'command', command: e.command.trim() }];
    return [];
  });
  return { answers, events, note: typeof src.note === 'string' ? src.note.slice(0, MAX_TEXT) : '', claimedComplete: src.status === 'complete' };
}

/** What a guest has in this browser (for the "continue where you left off?" prompt). */
export function guestSummary(guest) {
  guest.reload();
  const tickets = guest.raw().tickets || {};
  const started = SUPPORT_TICKETS.filter((t) => tickets[t.id] && (hasWork(tickets[t.id]) || tickets[t.id].status)).length;
  const completed = SUPPORT_TICKETS.filter((t) => tickets[t.id]?.status === 'complete').length;
  const practice = guest.raw().practice || {};
  const practiceStarted = hasPracticeWork(practice);
  const practiceCompleted = practice.status === 'complete';
  return { started, completed, practiceStarted, practiceCompleted, any: started > 0 || practiceStarted };
}

/** Throw the guest copy away (only after a successful claim, or when the learner chooses to). */
export function clearGuest(guest) {
  guest.store.remove('support-track');
  guest.reload();
}

/**
 * Move eligible guest Support Desk work into the learner record.
 * @returns {{ ok: boolean, results: {id: string, outcome: string}[], claimed: number, drafts: number, error?: string }}
 */
export function claimGuestSupport({ guest, learner }) {
  const results = [];
  try {
    guest.reload(); learner.reload();
    const guestData = guest.raw();
    const practice = guestData.practice;
    if (practice && hasPracticeWork(practice)) {
      const existing = learner.practiceRecord();
      if (existing.status === 'complete') results.push({ id: PRACTICE_TICKET.id, outcome: 'already-complete' });
      else if (hasPracticeWork(existing)) results.push({ id: PRACTICE_TICKET.id, outcome: 'kept-account-work' });
      else {
        const clean = sanitizePractice(practice);
        for (const [q, choice] of Object.entries(clean.answers)) learner.savePracticeAnswer(q, choice);
        for (const event of clean.events) learner.addPracticeEvent(event);
        learner.savePracticeNote(clean.note);
        if (clean.claimedComplete && learner.completePractice()) results.push({ id: PRACTICE_TICKET.id, outcome: 'claimed' });
        else results.push({ id: PRACTICE_TICKET.id, outcome: clean.claimedComplete ? 'not-validated-draft' : 'draft' });
      }
    }
    const tickets = guestData.tickets || {};
    for (const t of SUPPORT_TICKETS) {
      const g = tickets[t.id];
      const state = learner.ticketState(t.id);
      if (state === 'complete') { results.push({ id: t.id, outcome: 'already-complete' }); continue; }
      if (!g) break;
      if (state !== 'available') { results.push({ id: t.id, outcome: 'locked' }); break; }
      if (hasWork(learner.ticketRecord(t.id))) { results.push({ id: t.id, outcome: 'kept-account-work' }); break; }
      // work saved for an older version of this ticket's steps can't be re-validated: start it again
      if (recordVersion(g) !== ticketVersion(t)) { if (g.status === 'complete' || hasWork(g)) results.push({ id: t.id, outcome: 'updated-restart' }); break; }
      const { answers, guided, commands, claimedComplete } = sanitize(g, t);
      if (!Object.keys(answers).length && !Object.keys(guided).length && !commands.length) break;
      for (const [k, v] of Object.entries(answers)) learner.saveAnswer(t.id, k, v);
      for (const e of replayEvidence(t, commands)) learner.addEvidence(t.id, e.command, e.output);
      // evidence answers are checked against the REPLAYED output during validation
      for (const [k, v] of Object.entries(guided)) learner.saveGuided(t.id, k, v);
      if (claimedComplete && learner.complete(t.id)) { results.push({ id: t.id, outcome: 'claimed' }); continue; }
      results.push({ id: t.id, outcome: claimedComplete ? 'not-validated-draft' : 'draft' });
      break; // later tickets stay locked until this one is completed
    }
  } catch (e) {
    return { ok: false, results, claimed: 0, drafts: 0, error: String(e?.message || e) };
  }
  clearGuest(guest);
  return {
    ok: true, results,
    claimed: results.filter((r) => r.outcome === 'claimed').length,
    practiceClaimed: results.filter((r) => r.id === PRACTICE_TICKET.id && r.outcome === 'claimed').length,
    drafts: results.filter((r) => /draft/.test(r.outcome)).length,
  };
}
